import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  Plus, 
  PencilSimple, 
  Trash, 
  Globe,
  X,
  CheckCircle,
  WarningCircle
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultSiteForm = {
  site_key: "",
  domain: "",
  brand_name: "",
  tone_of_voice: "venlig og professionel",
  welcome_message: "Hej! Hvordan kan jeg hjælpe dig i dag?",
  ai_guidelines: "",
  return_policy_url: "",
  contact_url: "",
  booking_url: "",
  opening_hours: "",
  escalation_email: "",
  complaint_form_url: "",
  handoff_enabled: true,
  confidence_threshold: 0.7,
  // Widget styling
  widget_primary_color: "#4F46E5",
  widget_header_color: "#4338CA",
  widget_bubble_color: "#4F46E5",
  widget_user_message_color: "#4F46E5",
  widget_position: "right",
  widget_host_url: ""
};

export const SitesPage = ({ user, setUser }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [formData, setFormData] = useState(defaultSiteForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      toast.error("Kunne ikke hente sites");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedSite(null);
    setFormData(defaultSiteForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (site) => {
    setSelectedSite(site);
    setFormData({
      site_key: site.site_key,
      domain: site.domain,
      brand_name: site.brand_name,
      tone_of_voice: site.tone_of_voice || "venlig og professionel",
      welcome_message: site.welcome_message || "Hej! Hvordan kan jeg hjælpe dig i dag?",
      ai_guidelines: site.ai_guidelines || "",
      return_policy_url: site.return_policy_url || "",
      contact_url: site.contact_url || "",
      booking_url: site.booking_url || "",
      opening_hours: site.opening_hours || "",
      escalation_email: site.escalation_email || "",
      complaint_form_url: site.complaint_form_url || "",
      handoff_enabled: site.handoff_enabled ?? true,
      confidence_threshold: site.confidence_threshold || 0.7,
      // Widget styling
      widget_primary_color: site.widget_primary_color || "#4F46E5",
      widget_header_color: site.widget_header_color || "#4338CA",
      widget_bubble_color: site.widget_bubble_color || "#4F46E5",
      widget_user_message_color: site.widget_user_message_color || "#4F46E5",
      widget_position: site.widget_position || "right",
      widget_host_url: site.widget_host_url || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.site_key || !formData.domain || !formData.brand_name) {
      toast.error("Udfyld venligst alle påkrævede felter");
      return;
    }

    setSaving(true);
    try {
      const url = selectedSite 
        ? `${API}/sites/${selectedSite.site_key}`
        : `${API}/sites`;
      
      const method = selectedSite ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(selectedSite ? "Site opdateret" : "Site oprettet");
        setDialogOpen(false);
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke gemme site");
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error("Der opstod en fejl");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSite) return;

    try {
      const response = await fetch(`${API}/sites/${selectedSite.site_key}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Site slettet");
        setDeleteDialogOpen(false);
        setSelectedSite(null);
        fetchSites();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke slette site");
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Der opstod en fejl");
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="sites-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Sites
            </h1>
            <p className="text-slate-600">Administrer dine websites og chatbot konfigurationer</p>
          </div>
          <Button 
            onClick={handleOpenCreate}
            className="btn-primary"
            data-testid="create-site-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tilføj site
          </Button>
        </div>

        {/* Sites Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="card-default animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-slate-100 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sites.length === 0 ? (
          <Card className="card-default">
            <CardContent className="p-12 text-center">
              <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="duotone" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen sites endnu</h3>
              <p className="text-slate-600 mb-4">Opret dit første site for at komme i gang</p>
              <Button onClick={handleOpenCreate} className="btn-primary" data-testid="empty-create-site-btn">
                <Plus className="w-4 h-4 mr-2" />
                Opret site
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sites.map((site, index) => (
              <motion.div
                key={site.site_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="card-default hover:shadow-md hover:border-indigo-100 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-indigo-600" weight="duotone" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{site.brand_name}</h3>
                          <p className="text-sm text-slate-500">{site.domain}</p>
                        </div>
                      </div>
                      <div className={`status-dot ${site.status === 'active' ? 'status-dot-active' : 'status-dot-inactive'}`} />
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Site key</span>
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-slate-700">{site.site_key}</code>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Handoff</span>
                        <span className={site.handoff_enabled ? "text-emerald-600" : "text-slate-400"}>
                          {site.handoff_enabled ? "Aktiveret" : "Deaktiveret"}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleOpenEdit(site)}
                        data-testid={`edit-site-${site.site_key}`}
                      >
                        <PencilSimple className="w-4 h-4 mr-1" />
                        Rediger
                      </Button>
                      {user?.role === 'admin' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedSite(site);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-site-${site.site_key}`}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {selectedSite ? "Rediger site" : "Opret nyt site"}
              </DialogTitle>
              <DialogDescription>
                Konfigurer chatbot indstillinger for dit website
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site_key">Site key *</Label>
                  <Input
                    id="site_key"
                    placeholder="gardinshoppen"
                    value={formData.site_key}
                    onChange={(e) => setFormData({...formData, site_key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    disabled={!!selectedSite}
                    data-testid="site-key-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domæne *</Label>
                  <Input
                    id="domain"
                    placeholder="gardinshoppen.dk"
                    value={formData.domain}
                    onChange={(e) => setFormData({...formData, domain: e.target.value})}
                    data-testid="domain-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand_name">Brand navn *</Label>
                <Input
                  id="brand_name"
                  placeholder="Gardinshoppen"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                  data-testid="brand-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone_of_voice">Tone of voice</Label>
                <Input
                  id="tone_of_voice"
                  placeholder="venlig og professionel"
                  value={formData.tone_of_voice}
                  onChange={(e) => setFormData({...formData, tone_of_voice: e.target.value})}
                  data-testid="tone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="welcome_message">Velkomstbesked</Label>
                <Textarea
                  id="welcome_message"
                  placeholder="Hej! Hvordan kan jeg hjælpe dig i dag?"
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({...formData, welcome_message: e.target.value})}
                  data-testid="welcome-message-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai_guidelines">AI Retningslinjer</Label>
                <Textarea
                  id="ai_guidelines"
                  placeholder="- Svar altid på dansk
- Henvis til gardinshoppen.dk ved produktspørgsmål
- Ved prisforespørgsler, henvis til hjemmesiden
- Sig aldrig 'jeg ved ikke' - henvis til kundeservice
- Brug aldrig negative formuleringer om konkurrenter"
                  value={formData.ai_guidelines}
                  onChange={(e) => setFormData({...formData, ai_guidelines: e.target.value})}
                  className="min-h-[120px] font-mono text-sm"
                  data-testid="ai-guidelines-input"
                />
                <p className="text-xs text-slate-500">Specifikke instruktioner til AI'en. Skriv én retningslinje per linje.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="return_policy_url">Returpolitik URL</Label>
                  <Input
                    id="return_policy_url"
                    placeholder="https://..."
                    value={formData.return_policy_url}
                    onChange={(e) => setFormData({...formData, return_policy_url: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_url">Kontakt URL</Label>
                  <Input
                    id="contact_url"
                    placeholder="https://..."
                    value={formData.contact_url}
                    onChange={(e) => setFormData({...formData, contact_url: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="booking_url">Booking URL</Label>
                  <Input
                    id="booking_url"
                    placeholder="https://..."
                    value={formData.booking_url}
                    onChange={(e) => setFormData({...formData, booking_url: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="escalation_email">Eskalerings email</Label>
                  <Input
                    id="escalation_email"
                    placeholder="support@..."
                    value={formData.escalation_email}
                    onChange={(e) => setFormData({...formData, escalation_email: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="opening_hours">Åbningstider</Label>
                <Input
                  id="opening_hours"
                  placeholder="Man-Fre 9-17, Lør 10-14"
                  value={formData.opening_hours}
                  onChange={(e) => setFormData({...formData, opening_hours: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="complaint_form_url">Reklamationsformular URL</Label>
                <Input
                  id="complaint_form_url"
                  placeholder="https://..."
                  value={formData.complaint_form_url}
                  onChange={(e) => setFormData({...formData, complaint_form_url: e.target.value})}
                  data-testid="complaint-form-url-input"
                />
                <p className="text-xs text-slate-500">Link til ekstern reklamationsformular (defekte varer, skader, etc.)</p>
              </div>

              {/* Widget Styling Section */}
              <div className="border-t border-slate-200 pt-4 mt-2">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Widget Udseende</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="widget_header_color">Header farve</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="widget_header_color"
                        value={formData.widget_header_color}
                        onChange={(e) => setFormData({...formData, widget_header_color: e.target.value})}
                        className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                        data-testid="header-color-picker"
                      />
                      <Input
                        value={formData.widget_header_color}
                        onChange={(e) => setFormData({...formData, widget_header_color: e.target.value})}
                        className="flex-1 font-mono text-sm"
                        placeholder="#4338CA"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="widget_bubble_color">Chat-boble farve</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="widget_bubble_color"
                        value={formData.widget_bubble_color}
                        onChange={(e) => setFormData({...formData, widget_bubble_color: e.target.value})}
                        className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                        data-testid="bubble-color-picker"
                      />
                      <Input
                        value={formData.widget_bubble_color}
                        onChange={(e) => setFormData({...formData, widget_bubble_color: e.target.value})}
                        className="flex-1 font-mono text-sm"
                        placeholder="#4F46E5"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="space-y-2">
                    <Label htmlFor="widget_user_message_color">Bruger-besked farve</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        id="widget_user_message_color"
                        value={formData.widget_user_message_color}
                        onChange={(e) => setFormData({...formData, widget_user_message_color: e.target.value})}
                        className="w-10 h-10 rounded border border-slate-200 cursor-pointer"
                        data-testid="user-message-color-picker"
                      />
                      <Input
                        value={formData.widget_user_message_color}
                        onChange={(e) => setFormData({...formData, widget_user_message_color: e.target.value})}
                        className="flex-1 font-mono text-sm"
                        placeholder="#4F46E5"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Widget position</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={formData.widget_position === 'left' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFormData({...formData, widget_position: 'left'})}
                        className="flex-1"
                      >
                        Venstre
                      </Button>
                      <Button
                        type="button"
                        variant={formData.widget_position === 'right' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFormData({...formData, widget_position: 'right'})}
                        className="flex-1"
                      >
                        Højre
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Forhåndsvisning</p>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: formData.widget_bubble_color }}
                    >
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div 
                        className="h-8 rounded-t-lg flex items-center px-3"
                        style={{ backgroundColor: formData.widget_header_color }}
                      >
                        <span className="text-white text-xs font-medium">{formData.brand_name || 'Kundeservice'}</span>
                      </div>
                      <div className="bg-white rounded-b-lg p-2 border border-t-0 border-slate-200">
                        <div 
                          className="text-white text-xs px-2 py-1 rounded-lg inline-block"
                          style={{ backgroundColor: formData.widget_user_message_color }}
                        >
                          Hej, jeg har et spørgsmål
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Widget Host URL */}
                <div className="mt-4 space-y-2">
                  <Label htmlFor="widget_host_url">Widget Produktions-URL</Label>
                  <Input
                    id="widget_host_url"
                    value={formData.widget_host_url || ""}
                    onChange={(e) => setFormData({...formData, widget_host_url: e.target.value})}
                    placeholder="https://chat.dinvirksomhed.dk"
                    data-testid="widget-host-url"
                  />
                  <p className="text-xs text-slate-500">
                    Den URL hvor GardinBot er deployed i produktion. Bruges i embed-koden på "Embed Kode" siden.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Handoff til kundeservice</Label>
                  <p className="text-sm text-slate-500">Tillad chatbotten at sende samtaler til kundeservice</p>
                </div>
                <Switch
                  checked={formData.handoff_enabled}
                  onCheckedChange={(checked) => setFormData({...formData, handoff_enabled: checked})}
                  data-testid="handoff-switch"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuller
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-site-btn">
                {saving ? "Gemmer..." : (selectedSite ? "Gem ændringer" : "Opret site")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
              <AlertDialogDescription>
                Dette vil permanent slette sitet "{selectedSite?.brand_name}" og alle tilknyttede konfigurationer.
                Denne handling kan ikke fortrydes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuller</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Slet site
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
